import { Controller } from "@hotwired/stimulus";
import { createConsumer } from "@rails/actioncable";
import {
  billableActionsFromResponse,
  dispatchBillableActionsUsed,
} from "../utils/billableActionsMeter";
import {
  feedbackRowHtml,
  submitChatFeedback,
  buildSupportChatPrefill,
} from "./shared/chatFeedback";
import {
  AI_CHAT_MD_TABLE_WRAP_CLASS,
  formatMarkdownTables,
} from "../utils/aiChatMarkdown";
import { createChatLiveness } from "../utils/chatLiveness";

export default class extends Controller {
  static targets = ["input", "sendButton", "messages", "error"];

  static values = {
    url: String,
    brandDescription: String,
    brandTone: String,
    businessType: String,
    avatarUrl: String,
    userGravatarUrl: String,
    ctaId: Number,
    websiteId: Number,
    variantIds: Array,
    approxDaysLive: { type: Number, default: 0 },
    reportCheckouts: { type: Boolean, default: true },
    reportBookings: { type: Boolean, default: true },
    reportCustomGoal: { type: Boolean, default: false },
    customGoalLabel: { type: String, default: "Custom Goal" },
  };

  connect() {
    this.messagesHistory = [];
    this.experimentMode = null;
    this.metricGoalPending = false;
    this.cableConsumer = null;
    this.cableSubscription = null;

    if (!this.boundHandleKeydown) {
      this.boundHandleKeydown = this.handleKeydown.bind(this);
    }
    this.inputTarget.addEventListener("keydown", this.boundHandleKeydown);

    if (this.hasMessagesTarget) {
      if (!this.boundHandlePillClick) {
        this.boundHandlePillClick = this.handlePillClick.bind(this);
      }
      this.messagesTarget.addEventListener(
        "click",
        this.boundHandlePillClick,
        true
      );

      if (!this.boundHandleActionClick) {
        this.boundHandleActionClick = this.handleActionClick.bind(this);
      }
      this.messagesTarget.addEventListener(
        "click",
        this.boundHandleActionClick
      );

      if (!this.boundHandleThumbnailPreviewClick) {
        this.boundHandleThumbnailPreviewClick =
          this.handleThumbnailPreviewClick.bind(this);
      }
      this.messagesTarget.addEventListener(
        "click",
        this.boundHandleThumbnailPreviewClick
      );
    }

    this.boundHandleOpenEvent = this.handleOpenEvent.bind(this);
    document.addEventListener("variant-chat:open", this.boundHandleOpenEvent);

    this.renderPrefillIfEmpty();
  }

  teardownCable() {
    if (this.cableSubscription) {
      this.cableSubscription.unsubscribe();
      this.cableSubscription = null;
    }
    if (this.cableConsumer) {
      this.cableConsumer.disconnect();
      this.cableConsumer = null;
    }
    if (this.asyncTimeoutId) {
      clearTimeout(this.asyncTimeoutId);
      this.asyncTimeoutId = null;
    }
    this.rearmAsyncTimeout = null;
    if (this.chatLiveness) {
      this.chatLiveness.dispose();
      this.chatLiveness = null;
    }
  }

  disconnect() {
    this.teardownCable();
    if (this.asyncTimeoutId) {
      clearTimeout(this.asyncTimeoutId);
      this.asyncTimeoutId = null;
    }
    if (this.typewriterTimeoutId) {
      clearTimeout(this.typewriterTimeoutId);
      this.typewriterTimeoutId = null;
    }
    if (this.boundHandleKeydown) {
      this.inputTarget.removeEventListener("keydown", this.boundHandleKeydown);
    }
    if (this.hasMessagesTarget && this.boundHandlePillClick) {
      this.messagesTarget.removeEventListener(
        "click",
        this.boundHandlePillClick,
        true
      );
    }
    if (this.hasMessagesTarget && this.boundHandleActionClick) {
      this.messagesTarget.removeEventListener(
        "click",
        this.boundHandleActionClick
      );
    }
    if (this.hasMessagesTarget && this.boundHandleThumbnailPreviewClick) {
      this.messagesTarget.removeEventListener(
        "click",
        this.boundHandleThumbnailPreviewClick
      );
    }
    if (this.boundHandleOpenEvent) {
      document.removeEventListener(
        "variant-chat:open",
        this.boundHandleOpenEvent
      );
    }
    if (this.boundHandleEscape) {
      document.removeEventListener("keydown", this.boundHandleEscape);
      this.boundHandleEscape = null;
    }
  }

  handleOpenEvent() {
    this.openModal();
  }

  openModal() {
    this.element.classList.remove("hidden");
    this.element.classList.add("flex", "items-center", "justify-center");
    document.body.classList.add("modal-open");
    this.boundHandleEscape = this.handleEscape.bind(this);
    document.addEventListener("keydown", this.boundHandleEscape);
    requestAnimationFrame(() => this.inputTarget?.focus());
  }

  closeModal() {
    this.element.classList.add("hidden");
    this.element.classList.remove("flex", "items-center", "justify-center");
    document.body.classList.remove("modal-open");
    if (this.boundHandleEscape) {
      document.removeEventListener("keydown", this.boundHandleEscape);
      this.boundHandleEscape = null;
    }
    this.resetChat();
  }

  resetChat() {
    this.messagesHistory = [];
    this.experimentMode = null;
    this.metricGoalPending = false;
    if (this.typewriterTimeoutId) {
      clearTimeout(this.typewriterTimeoutId);
      this.typewriterTimeoutId = null;
    }
    if (this.hasMessagesTarget) {
      this.messagesTarget.innerHTML = "";
    }
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
      this.inputTarget.placeholder =
        "Describe the split-test you'd like to run...";
    }
    this.clearError();
    this.setLoading(false);
    this.renderPrefillIfEmpty();
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeModal();
    }
  }

  closeModalOnBackdrop(event) {
    const isOpen = !this.element.classList.contains("hidden");
    if (!isOpen) return;
    const content = this.element.querySelector("[data-variant-chat-content]");
    if (content && !content.contains(event.target)) {
      this.closeModal();
    }
  }

  renderPrefillIfEmpty() {
    if (!this.hasMessagesTarget) return;
    if (
      this.messagesTarget.children.length > 0 ||
      (this.messagesHistory || []).length > 0
    )
      return;

    this.appendAssistantMessage(
      "What type of split-test would you like to run?",
      { options: ["Remix an active variant", "Try a new template/layout"] },
      false
    );
  }

  handlePillClick(event) {
    const pill = event.target.closest(".variant-chat-option");
    if (!pill) return;
    const message = pill.closest("[data-variant-chat-assistant-message]");
    if (!message) return;
    if (message.hasAttribute("data-variant-chat-options-disabled")) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.disableOptionsInMessage(message);

    const option = pill.dataset.option;
    if (!option) return;

    if (option === "Remix an active variant") {
      this.experimentMode = "remix";
      this.appendUserMessage(option);
      this.messagesHistory.push({ role: "user", content: option });
      this.showPerformanceRecapThenAskMetric();
      return;
    } else if (option === "Try a new template/layout") {
      this.experimentMode = "new_template";
      this.appendUserMessage(option);
      this.messagesHistory.push({ role: "user", content: option });
      this.showPerformanceRecapThenAskMetric();
      return;
    }

    if (this.metricGoalPending) {
      this.metricGoalPending = false;
      this.inputTarget.value = `I want to improve ${option.toLowerCase()}`;
      this.submit();
      return;
    }

    this.inputTarget.value = option;
    this.submit();
  }

  showPerformanceRecapThenAskMetric() {
    const stats = this.buildExperimentStats();
    const campaign = stats.campaign || {};
    const variants = stats.variants || [];

    let recapLines = ["Here's how your campaign is currently performing:"];
    recapLines.push("");
    recapLines.push(
      [
        `**Campaign totals:** ${campaign.viewers || "0"} viewers`,
        `${campaign.conversions || "0"} conversions (${
          campaign.conversion_rate || "0%"
        })`,
        ...(this.reportCheckoutsValue
          ? [
              `${campaign.orders || "0"} orders`,
              `${campaign.revenue || "$0"} revenue`,
            ]
          : []),
        ...(this.reportBookingsValue
          ? [`${campaign.bookings || "0"} bookings`]
          : []),
        ...(this.reportCustomGoalValue
          ? [
              `${campaign.custom_goals || "0"} ${
                this.customGoalLabelValue || "Custom Goal"
              }`,
            ]
          : []),
      ].join(", ")
    );

    const formatVariantLine = (v) => {
      const name = v.name || `Variant ${v.id}`;
      const parts = [
        `${v.viewers || "0"} viewers`,
        `${v.conversions || "0"} conversions`,
        `${v.completions || "0"} completions`,
        ...(this.reportCheckoutsValue
          ? [`${v.orders || "0"} orders`, `${v.revenue || "$0"} revenue`]
          : []),
        ...(this.reportBookingsValue ? [`${v.bookings || "0"} bookings`] : []),
        ...(this.reportCustomGoalValue
          ? [
              `${v.custom_goals || "0"} ${
                this.customGoalLabelValue || "Custom Goal"
              }`,
            ]
          : []),
      ];
      return `- **${name}** (${v.traffic_percentage}% traffic): ${parts.join(
        ", "
      )}`;
    };

    const activeVariants = variants.filter(
      (v) => parseInt(v.traffic_percentage, 10) > 0
    );
    const inactiveVariants = variants.filter(
      (v) => parseInt(v.traffic_percentage, 10) <= 0
    );

    if (activeVariants.length > 0) {
      recapLines.push("");
      recapLines.push("**Active variants:**");
      activeVariants.forEach((v) => {
        recapLines.push(formatVariantLine(v));
      });
    }

    if (inactiveVariants.length > 0) {
      recapLines.push("");
      recapLines.push("**Inactive variants:**");
      inactiveVariants.forEach((v) => {
        recapLines.push(formatVariantLine(v));
      });
    }

    recapLines.push("");
    recapLines.push("Which metric would you like to improve?");

    const recapText = recapLines.join("\n");
    this.messagesHistory.push({ role: "assistant", content: recapText });
    this.metricGoalPending = true;

    this.appendAssistantMessage(
      recapText,
      {
        options: [
          "Views",
          "Conversions",
          "Completions",
          ...(this.reportCheckoutsValue ? ["Orders", "Revenue", "AOV"] : []),
          ...(this.reportBookingsValue ? ["Bookings"] : []),
          ...(this.reportCustomGoalValue
            ? [this.customGoalLabelValue || "Custom Goal"]
            : []),
        ],
      },
      false
    );
  }

  handleActionClick(event) {
    const btn = event.target.closest("[data-apply-experiment]");
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();

    try {
      const data = JSON.parse(decodeURIComponent(btn.dataset.applyExperiment));
      document.dispatchEvent(
        new CustomEvent("openDuplicateAndRemix", {
          detail: {
            variantId: data.variant_id,
            remix_instructions: data.remix_instructions,
            variant_name: data.variant_name,
          },
        })
      );
      this.closeModal();
    } catch (e) {
      console.error(
        "[variant_chat] Error dispatching openDuplicateAndRemix:",
        e
      );
    }
  }

  handleThumbnailPreviewClick(event) {
    const btn = event.target.closest("[data-template-preview-id]");
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const templateId = btn.dataset.templatePreviewId;
    if (!templateId) return;
    const remixPrompt = btn.dataset.remixPrompt || "";
    document.dispatchEvent(
      new CustomEvent("openVariantTemplatePreviewModal", {
        detail: {
          templateId: parseInt(templateId, 10),
          brandStyle: {},
          remixPrompt,
        },
      })
    );
  }

  buildExperimentStats() {
    const ctaId = this.ctaIdValue;
    const variantIds = this.variantIdsValue || [];

    const getText = (id) => {
      const el = document.getElementById(id);
      return el ? el.textContent.trim() : null;
    };

    const getValue = (id) => {
      const el = document.getElementById(id);
      return el ? el.value : null;
    };

    const campaign = {
      viewers: getText(`cta-${ctaId}-total-unique-viewers`),
      conversions: getText(`cta-${ctaId}-total-conversions`),
      conversion_rate: getText(`cta-${ctaId}-total-conversion-rate`),
      orders: getText(`cta-${ctaId}-total-orders`),
      revenue: getText(`cta-${ctaId}-total-revenue`),
      bookings: getText(`cta-${ctaId}-total-bookings`),
      custom_goals: getText(`cta-${ctaId}-total-custom-goals`),
      approx_days_live:
        this.approxDaysLiveValue > 0 ? this.approxDaysLiveValue : undefined,
    };

    const variants = variantIds.map((vid) => {
      const nameEl = document.querySelector(
        `[data-type="variant-name"][data-id="${vid}"] input[name="variant[name]"], #variant${vid}-name input[name="variant[name]"]`
      );
      return {
        id: vid,
        name: nameEl ? nameEl.value : null,
        traffic_percentage: getValue(`variant${vid}-percentage`),
        viewers: getText(`cta-${ctaId}-variant-${vid}-views`),
        conversions: getText(`cta-${ctaId}-variant-${vid}-conversions`),
        completions: getText(`cta-${ctaId}-variant-${vid}-completions`),
        orders: getText(`cta-${ctaId}-variant-${vid}-orders`),
        revenue: getText(`cta-${ctaId}-variant-${vid}-revenue`),
        bookings: getText(`cta-${ctaId}-variant-${vid}-bookings`),
        custom_goals: getText(`cta-${ctaId}-variant-${vid}-custom-goals`),
      };
    });

    return { campaign, variants };
  }

  async submit() {
    const promptText = this.inputTarget.value.trim();
    if (!promptText) return;

    this.setLoading(true);
    this.clearError();

    this.appendUserMessage(promptText);
    this.messagesHistory.push({ role: "user", content: promptText });
    this.inputTarget.value = "";

    this.showLoadingIndicator();

    try {
      this.teardownCable();

      const cableSyncId = Math.random().toString(36).substring(2, 15);
      this.cableConsumer = createConsumer();
      this.cableSubscription = this.cableConsumer.subscriptions.create(
        { channel: "AsyncModalRequestConfirmationChannel", id: cableSyncId },
        {
          connected: () => {
            if (this.chatLiveness) this.chatLiveness.schedule();
          },
          received: (data) => {
            if (this.asyncTimeoutId) {
              clearTimeout(this.asyncTimeoutId);
              this.asyncTimeoutId = null;
            }

            this.teardownCable();

            if (data.status === "ready") {
              dispatchBillableActionsUsed(billableActionsFromResponse(data));
              const content = this.buildAssistantContent(data);
              this.appendAssistantMessage(
                content.message,
                content.clarifyingQuestion,
                content.showChatFallback,
                content.experiment,
                content.recommendations,
                content.showChatFallback ? promptText : null
              );
              this.messagesHistory.push({
                role: "assistant",
                content: content.message,
              });
            } else {
              this.showError(
                data.error || "Something went wrong. Please try again."
              );
              const fallback =
                "Sorry, I couldn't process that. Please try again.";
              this.appendAssistantMessage(
                fallback,
                null,
                true,
                null,
                [],
                promptText
              );
              this.messagesHistory.push({
                role: "assistant",
                content: fallback,
              });
            }

            this.hideLoadingIndicator();
            this.setLoading(false);
          },
          rejected: () => {
            if (this.asyncTimeoutId) {
              clearTimeout(this.asyncTimeoutId);
              this.asyncTimeoutId = null;
            }

            if (this.sendButtonTarget.disabled) {
              this.teardownCable();
              this.showError(
                "Unable to establish a connection. Please try again."
              );
              const fallback =
                "I couldn't establish a stable connection. Please try sending your message again.";
              this.appendAssistantMessage(
                fallback,
                null,
                true,
                null,
                [],
                promptText
              );
              this.messagesHistory.push({
                role: "assistant",
                content: fallback,
              });
              this.hideLoadingIndicator();
              this.setLoading(false);
            }
          },
        }
      );

      this.chatLiveness = createChatLiveness({
        timeoutMs: 60000,
        isActive: () =>
          this.hasSendButtonTarget && this.sendButtonTarget.disabled,
        onTimeout: () => {
          this.teardownCable();
          this.showError(
            "The request is taking longer than expected. Please try again."
          );
          const fallback =
            "I'm having trouble responding right now. Please try sending your message again.";
          this.appendAssistantMessage(
            fallback,
            null,
            true,
            null,
            [],
            promptText
          );
          this.messagesHistory.push({
            role: "assistant",
            content: fallback,
          });
          this.hideLoadingIndicator();
          this.setLoading(false);
        },
      });
      this.rearmAsyncTimeout = () =>
        this.chatLiveness && this.chatLiveness.bump();

      await new Promise((resolve) => setTimeout(resolve, 500));

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
        },
        body: JSON.stringify({
          prompt: promptText,
          messages: this.messagesHistory.slice(0, -1),
          page_url: window.location.href,
          brand_description: this.brandDescriptionValue?.trim() || undefined,
          brand_tone: this.brandToneValue?.trim() || undefined,
          business_type: this.businessTypeValue?.trim() || undefined,
          experiment_stats: this.buildExperimentStats(),
          experiment_mode: this.experimentMode || "remix",
          cable_sync_id: cableSyncId,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        this.teardownCable();
        this.showError(data.error || "Something went wrong. Please try again.");
        const fallback = "Sorry, I couldn't process that. Please try again.";
        this.appendAssistantMessage(fallback, null, true, null, [], promptText);
        this.messagesHistory.push({ role: "assistant", content: fallback });
        this.hideLoadingIndicator();
        this.setLoading(false);
      }
    } catch (err) {
      this.teardownCable();
      this.showError("Unable to connect. Please try again.");
      const fallback =
        "I'm having trouble connecting. Please check your connection and try again.";
      this.appendAssistantMessage(fallback, null, true, null, [], promptText);
      this.messagesHistory.push({ role: "assistant", content: fallback });
      this.hideLoadingIndicator();
      this.setLoading(false);
    }
  }

  buildAssistantContent(data) {
    let message = data.message || "";
    const experiment = data.experiment || null;
    const recommendations = data.recommendations || [];
    const clarifyingQuestion = data.clarifying_question || null;
    const isFallback =
      !message &&
      !experiment &&
      recommendations.length === 0 &&
      !clarifyingQuestion;

    if (isFallback) {
      message =
        "Let me help you find the right split-test. What would you like to achieve?";
    }

    const showChatFallback =
      isFallback ||
      !!data.support_response ||
      this.messageSuggestsHumanSupport(message);

    return {
      message,
      experiment,
      recommendations,
      clarifyingQuestion,
      showChatFallback,
    };
  }

  messageSuggestsHumanSupport(text) {
    if (!text || typeof text !== "string") return false;
    const lower = text.toLowerCase();
    const patterns = [
      "convertflow team",
      "speak with someone",
      "talk to someone",
      "reach out",
      "contact support",
      "contact us",
      "personalized support",
      "human support",
      "support directly",
      "reaching out to convertflow",
    ];
    return patterns.some((p) => lower.includes(p));
  }

  appendUserMessage(text) {
    if (!this.hasMessagesTarget) return;
    const userGravatarUrl = this.userGravatarUrlValue || "";
    const div = document.createElement("div");
    div.className = "flex justify-end gap-2 mb-[32px]";
    div.innerHTML = `
      <div class="flex flex-col items-end min-w-0 max-w-[calc(85%-2.5rem)]">
        <div class="px-5 py-3 text-xl leading-relaxed text-gray-800"
             style="background-color: #F9F9F9; border: 1px solid #E5E5E5; border-radius: 12px 5px 12px 12px;">
          ${this.escapeHtml(text)}
        </div>
      </div>
      <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden bg-gray-200">
        <img src="${this.escapeHtml(
          userGravatarUrl
        )}" alt="" class="w-full h-full object-cover" />
      </div>
    `;
    this.messagesTarget.appendChild(div);
    this.scrollToBottom();
  }

  appendAssistantMessage(
    text,
    clarifyingQuestion,
    showFallback = false,
    experiment = null,
    recommendations = [],
    supportContext = null
  ) {
    if (!this.hasMessagesTarget) return;

    if (showFallback && supportContext) {
      this.supportContextForIntercom =
        this.intercomPrefillFromQuestion(supportContext);
    }

    const avatarUrl = this.avatarUrlValue || "";

    const builderUrls = clarifyingQuestion?.option_builder_urls || [];
    const clarifyingPillsHtml =
      clarifyingQuestion?.options?.length > 0
        ? clarifyingQuestion.options
            .map((opt, i) => {
              const label = typeof opt === "string" ? opt : String(opt ?? "");
              const normalizedLabel = this.normalizeAiText(label);
              const url = builderUrls[i];
              const escaped = this.escapeHtml(normalizedLabel);
              if (url && typeof url === "string") {
                return `<a href="${this.escapeHtml(
                  url
                )}" data-option="${escaped}" data-turbolinks="false" target="_blank" rel="noopener noreferrer" class="variant-chat-option inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xl transition-colors no-underline hover:opacity-90" style="background-color: #E6ECFF; border: 1px solid #D0D8FF; color: #003FFF;">${escaped}</a>`;
              }
              return `<button type="button" data-option="${escaped}" class="variant-chat-option inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xl transition-colors" style="background-color: #E6ECFF; border: 1px solid #D0D8FF; color: #003FFF;">${escaped}</button>`;
            })
            .join("")
        : "";

    const clarifyingSection = clarifyingPillsHtml
      ? `<div class="flex flex-wrap gap-2 mt-3">${clarifyingPillsHtml}</div>`
      : "";

    const experimentHtml = experiment
      ? this.buildExperimentActionHtml(experiment)
      : "";

    const recsHtml = (recommendations || [])
      .map((rec) => this.buildRecommendationHtml(rec))
      .filter(Boolean)
      .join("");

    const hasRecs = (recommendations || []).length > 0;
    const followUpPillsHtml = hasRecs
      ? `<button type="button" data-option="Recommend another template" class="variant-chat-option inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xl transition-colors cursor-pointer" style="background-color: #E6ECFF; border: 1px solid #D0D8FF; color: #003FFF;">Recommend another template</button>`
      : "";
    const followUpSection = followUpPillsHtml
      ? `<div class="flex flex-wrap gap-2 mt-3">${followUpPillsHtml}</div>`
      : "";

    const chatFallbackHtml =
      showFallback && typeof window.Intercom === "function"
        ? `<div class="flex flex-wrap gap-2 mt-3"><button type="button" data-action="click->variant-chat#openIntercomChat" class="variant-chat-option inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xl transition-colors cursor-pointer" style="background-color: #E6ECFF; border: 1px solid #D0D8FF; color: #003FFF;">Chat with us</button></div>`
        : showFallback
        ? `<p class="mt-2 mb-0 text-gray-600">Need help? Reach out to the ConvertFlow team for personalized support.</p>`
        : "";

    const formattedTextHtml = text
      ? `<div class="mb-3 [&>p:last-child]:mb-0">${this.formatParagraphs(
          text
        )}</div>`
      : "";

    const restHtml = [
      clarifyingSection,
      experimentHtml,
      recsHtml,
      followUpSection,
      chatFallbackHtml,
    ]
      .filter(Boolean)
      .join("");
    const useTypewriterEffect = text && text.trim().length > 0;

    // Only offer feedback on real assistant replies — not the intro prefill
    // (no user turn yet) or error fallbacks.
    const showFeedback =
      text &&
      text.trim().length > 0 &&
      !showFallback &&
      (this.messagesHistory || []).some((message) => message.role === "user");
    const feedbackHtml = showFeedback ? feedbackRowHtml("variant-chat") : "";

    const div = document.createElement("div");
    div.className = "flex justify-start gap-2 mb-[32px]";
    div.dataset.variantChatAssistantMessage = "";
    div.innerHTML = `
      <div class="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5 p-2" style="margin-top: 10px">
        <img src="${this.escapeHtml(
          avatarUrl
        )}" alt="" class="w-full h-full object-contain" />
      </div>
      <div class="flex flex-col min-w-0 max-w-[calc(85%-2.5rem)]">
        <div class="px-4 py-2.5 text-gray-800 leading-relaxed" style="border-radius: 5px 12px 12px 12px;">
          ${
            useTypewriterEffect
              ? `<div class="variant-chat-message-text text-xl text-gray-500 mb-3 [&>p:last-child]:mb-0"></div><div class="variant-chat-message-rest${
                  restHtml ? " opacity-0 max-h-0 overflow-hidden" : ""
                }">${restHtml}</div>`
              : `${formattedTextHtml}${restHtml}`
          }
        </div>
        ${feedbackHtml}
      </div>
    `;
    this.messagesTarget.appendChild(div);

    if (this.hasInputTarget) {
      this.inputTarget.placeholder = "Write your response";
    }

    if (useTypewriterEffect) {
      const textEl = div.querySelector(".variant-chat-message-text");
      const restEl = div.querySelector(".variant-chat-message-rest");
      this.runTypewriter(
        textEl,
        this.normalizeAiText(text),
        formattedTextHtml,
        () => {
          textEl.classList.remove("text-gray-500");
          if (restEl) {
            restEl.classList.remove("opacity-0", "max-h-0", "overflow-hidden");
          }
          this.disableAllOptionsExceptLatest();
          this.scrollToShowAssistantMessage(div);
        }
      );
    } else {
      this.disableAllOptionsExceptLatest();
      this.scrollToShowAssistantMessage(div);
    }
  }

  submitFeedback(event) {
    submitChatFeedback(event, {
      source: "experiment",
      messages: this.messagesHistory || [],
    });
  }

  buildExperimentActionHtml(experiment) {
    if (!experiment?.variant_id || !experiment?.remix_instructions) return "";

    const data = encodeURIComponent(
      JSON.stringify({
        variant_id: experiment.variant_id,
        remix_instructions: experiment.remix_instructions,
        variant_name: experiment.variant_name || "",
      })
    );

    return `
      <div class="mt-3 p-4 rounded-xl" style="border: 1px solid #E5E5E5; background: #FAFAFA;">
        <p class="text-xl font-semibold text-gray-800 mb-2">Suggested split-test</p>
        <p class="text-xl text-gray-600 mb-3">${this.escapeHtml(
          this.normalizeAiText(experiment.remix_instructions)
        )}</p>
        <button type="button"
          data-apply-experiment="${data}"
          class="variant-chat-option inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xl font-semibold text-white border-none cursor-pointer transition-opacity hover:opacity-90"
          style="background: linear-gradient(135deg, #4f6fff 0%, #003FFF 100%);">
          Apply Experiment &rarr;
        </button>
      </div>
    `;
  }

  buildRecommendationHtml(rec) {
    if (!rec) return "";

    const explanation = rec.explanation
      ? this.formatBold(this.escapeHtml(this.normalizeAiText(rec.explanation)))
      : "";

    const remixPrompt = rec.remix_prompt || "";
    const thumbnailCards = (rec.thumbnails || [])
      .map((thumb) => {
        const thumbUrl = typeof thumb === "string" ? thumb : thumb.url || "";
        const templateId = typeof thumb === "object" ? thumb.template_id : null;
        const img = `<img src="${this.escapeHtml(
          thumbUrl
        )}" alt="" class="w-full h-full object-cover rounded-lg" loading="lazy" />`;
        const remixAttr = remixPrompt
          ? ` data-remix-prompt="${this.escapeHtml(remixPrompt)}"`
          : "";
        const previewOverlay = `<span class="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" style="background: rgba(0,0,0,0.8);"><span class="border-solid border-[1px] border-white text-white text-xl font-semibold px-4 py-2 rounded-full" style="background: transparent;">Use Template →</span></span>`;
        const wrappedImg = templateId
          ? `<button type="button" class="relative w-full h-full min-w-0 min-h-0 border-none bg-transparent p-0 cursor-pointer" data-template-preview-id="${this.escapeHtml(
              String(templateId)
            )}"${remixAttr}>${img}${previewOverlay}</button>`
          : img;
        return `
          <div class="flex-shrink-0 flex flex-col items-center">
            <div class="rounded-2xl bg-gray-900 p-2 overflow-hidden flex items-center justify-center" style="width: 300px; max-width: 300px; height: 200px; max-height: 210px;">
              ${wrappedImg}
            </div>
          </div>`;
      })
      .filter(Boolean);

    const thumbnailsRow =
      thumbnailCards.length > 0
        ? `<div class="flex flex-nowrap gap-4 my-4 overflow-x-auto pb-2">${thumbnailCards.join(
            ""
          )}</div>`
        : "";

    let link = "";
    if (rec.link) {
      link = `<a href="${this.escapeHtml(
        rec.link
      )}" class="inline-block px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white hover:text-white font-semibold text-xl no-underline transition-colors" data-turbolinks="false" target="_blank" rel="noopener noreferrer">View Playbook &rarr;</a>`;
    }

    return `<div class="mt-2 leading-relaxed" style="border: 1px solid #E5E5E5; border-radius: 5px 12px 12px 12px; padding: 30px;">${
      explanation ? `<p class="mb-2 leading-relaxed">${explanation}</p>` : ""
    }${thumbnailsRow}${link}</div>`;
  }

  runTypewriter(container, plainText, formattedHtml, onComplete) {
    if (!container) {
      onComplete?.();
      return;
    }
    const words = plainText.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      container.innerHTML = formattedHtml;
      onComplete?.();
      return;
    }
    const delayMs = 28;
    let index = 0;
    const tick = () => {
      if (index >= words.length) {
        container.innerHTML = formattedHtml;
        onComplete?.();
        return;
      }
      container.textContent = words.slice(0, index + 1).join(" ");
      index += 1;
      this.scrollToBottom();
      this.typewriterTimeoutId = setTimeout(tick, delayMs);
    };
    if (this.typewriterTimeoutId) {
      clearTimeout(this.typewriterTimeoutId);
      this.typewriterTimeoutId = null;
    }
    tick();
  }

  disableAllOptionsExceptLatest() {
    if (!this.hasMessagesTarget) return;
    const messages = this.messagesTarget.querySelectorAll(
      "[data-variant-chat-assistant-message]"
    );
    messages.forEach((msg, i) => {
      if (i === messages.length - 1) {
        msg.removeAttribute("data-variant-chat-options-disabled");
      } else {
        msg.setAttribute("data-variant-chat-options-disabled", "true");
      }
    });
  }

  disableOptionsInMessage(messageEl) {
    if (messageEl) {
      messageEl.setAttribute("data-variant-chat-options-disabled", "true");
    }
  }

  scrollToShowAssistantMessage(element) {
    if (!this.hasMessagesTarget) return;
    const container = this.messagesTarget;
    requestAnimationFrame(() => {
      const padding = 16;
      const elementTopInContent =
        container.scrollTop +
        element.getBoundingClientRect().top -
        container.getBoundingClientRect().top;
      container.scrollTop = Math.max(0, elementTopInContent - padding);
    });
  }

  showLoadingIndicator() {
    if (!this.hasMessagesTarget) return;
    const avatarUrl = this.avatarUrlValue || "";
    const div = document.createElement("div");
    div.setAttribute("data-variant-chat-loading-indicator", "");
    div.className = "flex justify-start gap-2 mb-[32px]";
    div.innerHTML = `
      <div class="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5 p-2" style="margin-top: 10px">
        <img src="${this.escapeHtml(
          avatarUrl
        )}" alt="" class="w-full h-full object-contain" />
      </div>
      <div class="flex flex-col min-w-0 max-w-[calc(85%-2.5rem)]">
        <div class="px-4 py-2.5 text-xl text-gray-800 leading-relaxed flex items-center gap-1 min-h-[2.5rem]" style="border-radius: 5px 12px 12px 12px;">
          <span class="playbook-chat-typing-dots">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </div>
      </div>
    `;
    this.messagesTarget.appendChild(div);
    this.scrollToBottom();
  }

  hideLoadingIndicator() {
    if (!this.hasMessagesTarget) return;
    const el = this.messagesTarget.querySelector(
      "[data-variant-chat-loading-indicator]"
    );
    if (el) el.remove();
  }

  setLoading(loading) {
    this.sendButtonTarget.disabled = loading;
    this.sendButtonTarget.classList.toggle("opacity-50", loading);
    this.sendButtonTarget.classList.toggle("cursor-not-allowed", loading);
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = "";
      this.errorTarget.classList.add("hidden");
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    }
  }

  escapeHtml(text) {
    return String(text ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  formatBold(text) {
    if (!text) return "";
    return text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  }

  /** Renders markdown ## headings as bold (e.g. ## What to do next). */
  formatMarkdownHeadings(text) {
    if (!text) return "";
    return text.replace(
      /^##\s+(.+)$/gm,
      '<strong class="font-semibold text-gray-900 text-xl mb-3">$1</strong>'
    );
  }

  /**
   * Models often emit **What to do next** instead of ##; without this it reads as tiny inline bold.
   */
  formatStandaloneBoldSectionTitles(text) {
    if (!text) return "";
    const cls = "font-semibold text-gray-900 text-xl mb-3 mt-1 first:mt-0";
    const titles = ["What to do next", "Suggested split-test"];
    let out = text;
    titles.forEach((title) => {
      const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      out = out.replace(
        new RegExp(`^\\*\\*${escapedTitle}\\*\\*\\s*$`, "gim"),
        `<strong class="${cls}">${title}</strong>`
      );
    });
    return out;
  }

  /** Decodes URL-encoded sequences (e.g. %26 -> &). */
  decodeUrlEncoded(text) {
    if (!text || typeof text !== "string") return text;
    try {
      return text.replace(/%([0-9A-Fa-f]{2})/g, (_, hex) =>
        String.fromCharCode(parseInt(hex, 16))
      );
    } catch {
      return text;
    }
  }

  /** Decodes HTML entities (e.g. &amp; -> &) before escaping for safe render. */
  decodeHtmlEntities(text) {
    if (!text || typeof text !== "string") return text;
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  }

  /** Normalizes common AI output encodings prior to display formatting. */
  normalizeAiText(text) {
    if (!text || typeof text !== "string") return text;
    let normalized = text;
    // Some model outputs can be double-encoded (e.g. &amp;amp;); decode in capped passes.
    for (let i = 0; i < 3; i += 1) {
      const next = this.decodeHtmlEntities(this.decodeUrlEncoded(normalized));
      if (next === normalized) break;
      normalized = next;
    }
    return normalized;
  }

  formatParagraphs(text) {
    if (!text || typeof text !== "string") return "";
    const escaped = this.escapeHtml(this.normalizeAiText(text));
    const withHeadings = this.formatMarkdownHeadings(escaped);
    const withSectionTitles =
      this.formatStandaloneBoldSectionTitles(withHeadings);
    const withBold = this.formatBold(withSectionTitles);
    const withLinks = this.formatLinks(withBold);
    const withTables = formatMarkdownTables(withLinks);
    const tableSplit = new RegExp(
      `(<div class="${AI_CHAT_MD_TABLE_WRAP_CLASS}[\\s\\S]*?<\\/div>)`
    );
    return withTables
      .split(tableSplit)
      .map((part) => {
        if (part.startsWith(`<div class="${AI_CHAT_MD_TABLE_WRAP_CLASS}`)) {
          return part;
        }
        return this.wrapTextParagraphs(part);
      })
      .join("");
  }

  wrapTextParagraphs(text) {
    const trimmed = String(text || "").replace(/^\n+|\n+$/g, "");
    if (!trimmed.trim()) return "";
    const paragraphs = trimmed
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter(Boolean);
    const headingOnly = (p) =>
      /^<strong class="[^"]*text-xl[^"]*">[^<]+<\/strong>$/.test(p);
    if (paragraphs.length <= 1) {
      const single = paragraphs[0] || trimmed;
      if (headingOnly(single)) {
        return single;
      }
      return `<p class="mb-0 leading-relaxed">${single.replace(
        /\n/g,
        "<br>"
      )}</p>`;
    }
    return paragraphs
      .map((p) => {
        if (headingOnly(p)) {
          return p;
        }
        return `<p class="mb-2 leading-relaxed last:mb-0">${p.replace(
          /\n/g,
          "<br>"
        )}</p>`;
      })
      .join("");
  }

  formatLinks(text) {
    if (!text) return "";
    let out = text.replace(/\[([^\]]*)\]\(([^)]+)\)/g, (_, linkText, url) => {
      const trimmed = url.trim();
      if (
        trimmed.startsWith("/") ||
        trimmed.startsWith("https://") ||
        trimmed.startsWith("http://")
      ) {
        const safeLinkText = this.escapeHtml(this.normalizeAiText(linkText));
        return `<a href="${this.escapeHtml(
          trimmed
        )}" class="text-blue-600 hover:text-blue-800 font-semibold underline" target="_blank" rel="noopener noreferrer">${safeLinkText}</a>`;
      }
      return `[${linkText}](${url})`;
    });
    // Linkify any bare support.convertflow.com URL (so plain URLs still become clickable)
    out = out.replace(
      /(^|[\s>])(https:\/\/support\.convertflow\.com\/[^\s<>"]+)/g,
      (_, before, url) =>
        `${before}<a href="${this.escapeHtml(
          url
        )}" class="text-blue-600 hover:text-blue-800 font-semibold underline" target="_blank" rel="noopener noreferrer">${this.escapeHtml(
          url
        )}</a>`
    );
    return out;
  }

  openIntercomChat() {
    if (typeof window.Intercom !== "function") return;

    const prefill =
      this.supportContextForIntercom ||
      buildSupportChatPrefill(
        this.messagesHistory,
        "I need help with my split-test"
      );
    window.Intercom("showNewMessage", prefill);
  }

  intercomPrefillFromQuestion(question) {
    if (!question || typeof question !== "string") return null;
    const trimmed = question.trim().replace(/\s+/g, " ");
    if (trimmed.length <= 80) return trimmed;
    return trimmed.slice(0, 77) + "...";
  }
}
